import {
    Button,
    dispatchCustomEvent,
    Text as FoldText,
    Icon,
    stopEvent,
    useCustomEvent,
    useId,
    View,
} from '@fold-dev/core'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import TextAlign from '@tiptap/extension-text-align'
import Typography from '@tiptap/extension-typography'
import { BubbleMenu, EditorContent, Extension, ReactRenderer, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import {
    LuAlignCenter,
    LuAlignLeft,
    LuAlignRight,
    LuBold,
    LuHeading1,
    LuHeading2,
    LuHeading3,
    LuItalic,
    LuStrikethrough,
} from 'react-icons/lu'
import { PiParagraph } from 'react-icons/pi'
import { TbList, TbListNumbers } from 'react-icons/tb'
import tippy from 'tippy.js'
import { useProjectUsers, useTeamUsers, useWorkspaceUsers } from '../hooks/api.hooks'
import { isString } from '../helpers'

const MenuBar = ({ editor }) => {
    return (
        <View
            row
            onMouseDown={stopEvent}
            onClick={stopEvent}
            className="tiptap-menubar">
            <Button
                size="xs"
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                active={editor.isActive('heading', { level: 1 })}>
                <Icon icon={LuHeading1} />
            </Button>
            <Button
                size="xs"
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                active={editor.isActive('heading', { level: 2 })}>
                <Icon icon={LuHeading2} />
            </Button>
            <Button
                size="xs"
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                active={editor.isActive('heading', { level: 3 })}>
                <Icon icon={LuHeading3} />
            </Button>
            <Button
                size="xs"
                onClick={() => editor.chain().focus().setParagraph().run()}
                active={editor.isActive('paragraph')}>
                <Icon icon={PiParagraph} />
            </Button>
            <Button
                size="xs"
                onClick={() => editor.chain().focus().toggleBold().run()}
                active={editor.isActive('bold')}>
                <Icon icon={LuBold} />
            </Button>
            <Button
                size="xs"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                active={editor.isActive('italic')}>
                <Icon icon={LuItalic} />
            </Button>
            <Button
                size="xs"
                onClick={() => editor.chain().focus().toggleStrike().run()}
                active={editor.isActive('strike')}>
                <Icon icon={LuStrikethrough} />
            </Button>
            {/*             <Button
                size="xs"
                onClick={() => editor.chain().focus().toggleHighlight().run()}
                active={editor.isActive('highlight')}>
                <Icon icon={LuHighlighter} />
            </Button> */}
            <Button
                size="xs"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={editor.isActive('bulletList') ? 'is-active' : ''}>
                <Icon icon={TbList} />
            </Button>
            <Button
                size="xs"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={editor.isActive('orderedList') ? 'is-active' : ''}>
                <Icon icon={TbListNumbers} />
            </Button>
            <Button
                size="xs"
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                active={editor.isActive({ textAlign: 'left' })}>
                <Icon icon={LuAlignLeft} />
            </Button>
            <Button
                size="xs"
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                active={editor.isActive({ textAlign: 'center' })}>
                <Icon icon={LuAlignCenter} />
            </Button>
            <Button
                size="xs"
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                active={editor.isActive({ textAlign: 'right' })}>
                <Icon icon={LuAlignRight} />
            </Button>
        </View>
    )
}

const MentionList = forwardRef((props: any, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)

    const selectItem = (index) => {
        const item = props.items[index]

        if (item) {
            props.command({ id: item })
        }
    }

    const upHandler = () => {
        setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
    }

    const downHandler = () => {
        setSelectedIndex((selectedIndex + 1) % props.items.length)
    }

    const enterHandler = () => {
        selectItem(selectedIndex)
    }

    useEffect(() => setSelectedIndex(0), [props.items])

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }) => {
            if (event.key === 'ArrowUp') {
                upHandler()
                return true
            }

            if (event.key === 'ArrowDown') {
                downHandler()
                return true
            }

            if (event.key === 'Enter') {
                enterHandler()
                return true
            }

            return false
        },
    }))

    return (
        <div className="dropdown-menu">
            {props.items.length ? (
                props.items.map((item, index) => (
                    <FoldText
                        key={index}
                        colorToken={index === selectedIndex ? 'accent' : undefined}
                        bgToken={index === selectedIndex ? 'surface-strong' : undefined}
                        onClick={() => selectItem(index)}>
                        {item}
                    </FoldText>
                ))
            ) : (
                <div className="item">No result</div>
            )}
        </div>
    )
})

export const TIPTAP_CONTENT_SAVE_TIMEOUT = 750

export const Tiptap = forwardRef((props: any, ref: any) => {
    const {
        content,
        onUpdate = null,
        onSubmit = null,
        hideToolbar = false,
        projectId = null,
        workspaceId = null,
        teamId = null,
        editorProps = {},
        updateAfterBlur = false,
    } = props
    const instanceId = useId(props.instanceId)
    const projectUsersAPI = useProjectUsers()
    const workspaceUsersAPI = useWorkspaceUsers()
    const teamUsersAPI = useTeamUsers()
    const contentCache = useRef('')
    const editor = useEditor(
        {
            // immediatelyRender: true,
            // shouldRerenderOnTransaction: false,
            content: '',
            extensions: [
                // Document,
                // Paragraph,
                // Text,
                StarterKit,
                Placeholder.configure({
                    placeholder: 'Write something ...',
                }),
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
                Link.configure({
                    openOnClick: true,
                    autolink: true,
                    defaultProtocol: 'https',
                }),
                Highlight,
                Typography,
                TaskList,
                TaskItem.configure({
                    nested: true,
                }),
                Mention.configure({
                    HTMLAttributes: { class: 'mention' },
                    suggestion: {
                        items: async ({ query }) => {
                            const users = teamId
                                ? await teamUsersAPI.getSearch(workspaceId, teamId, query)
                                : projectId
                                ? await projectUsersAPI.getSearch(workspaceId, projectId, query)
                                : await workspaceUsersAPI.getSearch(workspaceId, query)

                            return users.map((user) => user.username)
                        },

                        render: () => {
                            let reactRenderer
                            let popup

                            return {
                                onStart: (props) => {
                                    if (!props.clientRect) {
                                        return
                                    }

                                    reactRenderer = new ReactRenderer(MentionList, {
                                        props,
                                        editor: props.editor,
                                    })

                                    popup = tippy('body', {
                                        getReferenceClientRect: props.clientRect,
                                        appendTo: () => document.body,
                                        content: reactRenderer.element,
                                        showOnCreate: true,
                                        interactive: true,
                                        trigger: 'manual',
                                        placement: 'bottom-start',
                                    })
                                },

                                onUpdate(props) {
                                    reactRenderer.updateProps(props)

                                    if (!props.clientRect) {
                                        return
                                    }

                                    popup[0].setProps({
                                        getReferenceClientRect: props.clientRect,
                                    })
                                },

                                onKeyDown(props) {
                                    if (props.event.key === 'Escape') {
                                        popup[0]?.hide()

                                        return true
                                    }

                                    return reactRenderer?.ref?.onKeyDown(props)
                                },

                                onExit() {
                                    popup[0]?.destroy()
                                    reactRenderer?.destroy()
                                },
                            }
                        },
                    },
                }),
                Extension.create({
                    addKeyboardShortcuts() {
                        return {
                            'Cmd-Enter'({ editor }) {
                                dispatchCustomEvent('tiptap:submit', { instanceId })
                                return true
                            },
                            'Ctrl-Enter'({ editor }) {
                                dispatchCustomEvent('tiptap:submit', { instanceId })
                                return true
                            },
                        }
                    },
                }),
            ],
            onUpdate({ editor }) {
                if (onUpdate) onUpdate(editor.getHTML(), editor.getJSON(), editor)
            },
            onBlur: ({ editor }) => {
                // if the editor blurs, then update the content from the latest state
                // this will pull in the latest changes have happened
                // but wait for state to update first
                if (updateAfterBlur) {
                    setTimeout(
                        () => editor.commands.setContent(contentCache.current),
                        TIPTAP_CONTENT_SAVE_TIMEOUT + 250
                    )
                }
            },
            onBeforeCreate({ editor }) {
                // Before the view is created.
            },
            onCreate({ editor }) {
                // The editor is ready.
            },
            onSelectionUpdate({ editor }) {
                // The selection has changed.
            },
            onTransaction({ editor, transaction }) {
                // The editor state has changed.
            },
            onFocus({ editor, event }) {
                // The editor is focused.
            },
            onDestroy() {
                // The editor is being destroyed.
            },
            onPaste(event: ClipboardEvent, slice) {
                // The editor is being pasted into.
            },
            onDrop(event: DragEvent, slice, moved: boolean) {
                // The editor is being pasted into.
            },
            onContentError({ editor, error, disableCollaboration }) {
                // The editor content does not match the schema.
            },
        },
        [workspaceId, projectId, teamId, updateAfterBlur]
    )

    useCustomEvent('tiptap:mention', ({ detail }) => {
        if (onSubmit && instanceId == detail.instanceId) {
            editor.chain().focus().insertContent('@').run()
        }
    })

    useCustomEvent('tiptap:emoji', ({ detail }) => {
        if (onSubmit && instanceId == detail.instanceId) {
            editor.chain().focus().insertContent(detail.emoji).run()
        }
    })

    useCustomEvent('tiptap:submit', ({ detail }) => {
        if (onSubmit && instanceId == detail.instanceId) {
            onSubmit(editor.getHTML(), editor.getJSON(), editor)
        }
    })

    useEffect(() => {
        if (!!editor && isString(content)) {
            // don't update the editor if the content is being edited
            if (!editor.isFocused) editor.commands.setContent(content)
        }

        // cache this for the onblur
        if (isString(content)) contentCache.current = content
    }, [content, editor])

    return (
        <div className="reveal-on-hover tiptap-editor__container">
            <div className="tiptap-editor">
                {editor && (
                    <BubbleMenu
                        className="bubble-menu"
                        tippyOptions={{ duration: 100 }}
                        editor={editor}>
                        <MenuBar editor={editor} />
                    </BubbleMenu>
                )}

                <EditorContent
                    editor={editor}
                    ref={ref}
                    {...editorProps}
                />
            </div>
            {/* {(!hideToolbar && editor) && <MenuBar editor={editor} />} */}
        </div>
    )
})
